'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { stegaClean } from 'next-sanity';
import type { PageGeneralRulesV3Schema } from '@/sanity/pages/PageGeneralRulesV3Template';
import SearchBar from './SearchBar';
import NoteBar from './NoteBar';
import RulesSidebar, { type SidebarGroup } from './RulesSidebar';
import RuleSelectMobile from './RuleSelectMobile';
import RuleDetail from './RuleDetail';
import { flattenRules } from './search';

const RULE_PARAM = 'rule';

function readRuleFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const fromQuery = new URLSearchParams(window.location.search).get(RULE_PARAM);
  if (fromQuery) return fromQuery;
  const hash = window.location.hash.replace(/^#/, '');
  return hash || null;
}

function writeRuleToUrl(slug: string) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set(RULE_PARAM, slug);
  url.hash = '';
  window.history.replaceState(window.history.state, '', url.toString());
}

export default function RulesBody({ data }: { data: PageGeneralRulesV3Schema }) {
  const language = stegaClean(data.settings?.language);
  const flat = useMemo(() => flattenRules(data.groups), [data.groups]);
  const groups: SidebarGroup[] = useMemo(
    () =>
      (data.groups ?? []).map((g) => ({
        key: g._key,
        title: stegaClean(g.title) ?? '',
        rules: flat.filter((r) => (g.rules ?? []).some((rr) => rr._key === r.rule._key)),
      })),
    [data.groups, flat],
  );

  const [activeSlug, setActiveSlug] = useState<string>(flat[0]?.slug ?? '');
  const [variantIndex, setVariantIndex] = useState(0);
  const detailRef = useRef<HTMLDivElement>(null);
  const hydratedRef = useRef(false);

  // Deep links: /rules?rule=daily-loss-limit (or #daily-loss-limit).
  useEffect(() => {
    const fromUrl = readRuleFromUrl();
    if (fromUrl && flat.some((r) => r.slug === fromUrl)) setActiveSlug(fromUrl);
    hydratedRef.current = true;
  }, [flat]);

  const select = useCallback(
    (slug: string, { scroll = false }: { scroll?: boolean } = {}) => {
      if (!flat.some((r) => r.slug === slug)) return;
      setActiveSlug(slug);
      setVariantIndex(0);
      writeRuleToUrl(slug);
      if (scroll) {
        requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      }
    },
    [flat],
  );

  // Links inside rule copy/figures that point at another rule (`?rule=buffer`)
  // switch in place instead of reloading the page.
  const onBodyClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const anchor = (e.target as HTMLElement).closest('a[href]');
      if (!anchor || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
      const href = anchor.getAttribute('href') ?? '';
      const match = href.match(/^(?:[^?#]*)\?rule=([a-z0-9-]+)$/i);
      if (!match) return;
      e.preventDefault();
      select(match[1], { scroll: true });
    },
    [select],
  );

  const activeIndex = Math.max(
    0,
    flat.findIndex((r) => r.slug === activeSlug),
  );
  const active = flat[activeIndex];
  const next = flat[activeIndex + 1];

  if (!active) return null;

  return (
    <section
      data-ui="rules-v3.body"
      className="relative w-full bg-white pb-[8rem] pt-0 [text-wrap:wrap] lg:pb-[15rem] lg:pt-[3rem]"
      onClick={onBodyClick}
    >
      <div className="mx-auto flex w-[calc(100%-4rem)] flex-col items-center gap-[3rem] lg:w-[146rem] lg:gap-[5rem]">
        <SearchBar
          rules={flat}
          placeholder={stegaClean(data.searchPlaceholder)}
          rulesLabel={stegaClean(data.searchRulesLabel)}
          mentionedLabel={stegaClean(data.searchMentionedLabel)}
          resultsLabel={stegaClean(data.searchResultsLabel)}
          emptyLabel={stegaClean(data.searchEmptyLabel)}
          onSelect={(slug) => select(slug, { scroll: true })}
        />

        <NoteBar
          content={data.noteContent}
          linkLabel={data.noteLinkLabel}
          link={data.noteLink}
          language={language}
        />

        <div className="flex w-full flex-col gap-[3rem] lg:flex-row lg:items-start lg:gap-[8rem]">
          <div className="w-full lg:hidden">
            <RuleSelectMobile
              label={stegaClean(data.ruleSelectLabel)}
              groups={groups}
              activeSlug={active.slug}
              onSelect={(slug) => select(slug)}
            />
          </div>
          {/* self-stretch: the wrapper must span the whole row, or the sticky nav has nowhere to stick. */}
          <div className="hidden lg:block lg:self-stretch">
            <RulesSidebar groups={groups} activeSlug={active.slug} onSelect={(slug) => select(slug)} />
          </div>

          <div className="min-w-0 flex-1">
            <RuleDetail
              ref={detailRef}
              rule={active.rule}
              variantIndex={variantIndex}
              onVariantChange={setVariantIndex}
              nextRule={next ? { slug: next.slug, title: next.title } : undefined}
              onNext={(slug) => select(slug, { scroll: true })}
              appliesToLabel={stegaClean(data.appliesToLabel)}
              nextRuleLabel={stegaClean(data.nextRuleLabel)}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
